import { getPresignedUrl, uploadToStorage } from "@/api";
import { DeleteUpload } from "@/components/shared/media/DeleteUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Alert,
    AlertTitle,
    alpha,
    Box,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { filesize } from "filesize";
import { Upload } from "lucide-react";
import { useCallback, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { IImportFormData } from "./types";

const DEFAULT_MAX_SIZE = 200 * 1024 * 1024; // 200 MB

interface IProps {
    formData: IImportFormData;
    onFormDataChange: (formData: IImportFormData) => void;
}

export const UploadStep = ({ formData, onFormDataChange }: IProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);

    // Helper function to update form data
    const updateFormData = useCallback(
        (updates: Partial<IImportFormData>) => {
            onFormDataChange({ ...formData, ...updates });
        },
        [formData, onFormDataChange]
    );

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            setUploadProgress(0);
            const presignedUrl = await getPresignedUrl({
                filename: file.name,
                content_type: file.type,
            });
            const objectName = await uploadToStorage(
                file,
                presignedUrl,
                (progressEvent: AxiosProgressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                }
            );
            return objectName;
        },
        onSuccess: (objectName) => {
            updateFormData({ objectName });
        },
    });

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            setIsDragging(false);

            if (fileRejections.length > 0) {
                setError(
                    `File upload failed: ${fileRejections[0].errors[0].message}`
                );
                return;
            }

            if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                setError(null);
                updateFormData({ file });
                uploadMutation.mutate(file);
            }
        },
        [uploadMutation, updateFormData]
    );

    const handleFileRemove = () => {
        updateFormData({ file: undefined, objectName: undefined });
        setError(null);
        setUploadProgress(null);
        uploadMutation.reset();
    };

    const { getRootProps, getInputProps } = useDropzone({
        maxFiles: 1,
        accept: {
            "application/vnd.ms-excel": [".xlsx"],
        },
        maxSize: DEFAULT_MAX_SIZE,
        onDrop,
        onDragOver,
        onDragLeave,
    });

    return (
        <Stack gap={1}>
            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ width: "100%" }}>
                    <AlertTitle>Error</AlertTitle>
                    {error}
                </Alert>
            )}

            {/* File Drop */}
            {!formData.file && (
                <Box
                    sx={{
                        border: 2,
                        p: 4,
                        borderStyle: "dashed",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        bgcolor: (theme) =>
                            isDragging
                                ? alpha(theme.palette.secondary.main, 0.1)
                                : undefined,
                        borderColor: (theme) =>
                            isDragging
                                ? theme.palette.secondary.main
                                : theme.palette.gray.light,
                        color: (theme) => theme.palette.gray.main,
                        "&:hover": {
                            borderColor: (theme) => theme.palette.gray.main,
                        },
                    }}
                    {...getRootProps()}
                >
                    <input {...getInputProps()} />

                    <Stack alignItems="center" gap={1} textAlign="center">
                        <Upload fontSize="large" width={48} height={48} />
                        <Typography
                            variant="body2"
                            color="text.primary"
                            fontWeight={500}
                        >
                            Click to upload or drag and drop
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            XLSX files only (max. 200 MB)
                        </Typography>
                    </Stack>
                </Box>
            )}

            {/* Uploaded File */}
            {formData.file && (
                <Stack
                    direction={"row"}
                    sx={{
                        width: "100%",
                        gap: 3,
                        alignItems: "center",
                        p: 2,
                        border: 1,
                        borderColor: (theme) => theme.palette.gray.light,
                        borderRadius: 2,
                    }}
                >
                    <UploadFileIcon
                        sx={{
                            width: 24,
                            height: 24,
                            mb: 1,
                            color: (theme) => theme.palette.primary.main,
                        }}
                    />

                    <Stack>
                        <Typography>{formData.file.name}</Typography>
                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            <Typography
                                variant={"body2"}
                                color={"textSecondary"}
                            >
                                {filesize(formData.file.size, { round: 1 })}
                            </Typography>
                            {uploadMutation.isPending &&
                                typeof uploadProgress === "number" &&
                                !uploadMutation.isError && (
                                    <>
                                        <Typography
                                            color={"textSecondary"}
                                            variant={"body2"}
                                        >
                                            •
                                        </Typography>
                                        <Typography
                                            color={"textSecondary"}
                                            variant={"body2"}
                                        >{`${uploadProgress}%`}</Typography>
                                    </>
                                )}
                            {uploadMutation.isError && (
                                <>
                                    <Typography
                                        color={"textSecondary"}
                                        variant={"body2"}
                                    >
                                        •
                                    </Typography>
                                    <ErrorOutlineIcon
                                        sx={{ width: 16, height: 16, mr: -0.5 }}
                                        color={"error"}
                                    />
                                    <Typography
                                        color={"error"}
                                        variant={"body2"}
                                    >
                                        Failed to upload
                                    </Typography>
                                </>
                            )}
                        </Stack>
                        {(uploadMutation.isPending ||
                            uploadMutation.isError) && (
                            <LinearProgress
                                sx={{ width: 200, borderRadius: 8, mt: 0.5 }}
                                variant={"determinate"}
                                value={uploadProgress ?? 0}
                                color={
                                    uploadMutation.isError ? "error" : "primary"
                                }
                            />
                        )}
                    </Stack>

                    <Box flexGrow={1} />

                    {(!uploadMutation.isPending || uploadMutation.isError) && (
                        <DeleteUpload onDelete={handleFileRemove} />
                    )}
                </Stack>
            )}
        </Stack>
    );
};
